import * as React from 'react';

import { cn } from '../../lib/utils';
import { useActivePath } from '../../hooks/use-active-path';
import type { NavigationItem } from '../../types/navigation';
import { SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '../ui/sidebar';

export interface SidebarNavProps {
  /** CSS classes applied to the outer `<SidebarGroup>`. */
  className?: string;
  /** Optional function to check if the user has a specific permission.
   *  When provided, items with a `requiredPermission` are only rendered
   *  if this function returns true for that permission.
   *  When not provided, all items are rendered (backward compatible). */
  hasPermission?: (permission: string) => boolean;
  /** Navigation items to render. */
  items: NavigationItem[];
  /**
   * Render function that produces a routable link for each item.
   * The component is router-agnostic — the consumer provides the link implementation
   * (e.g. TanStack `<Link>`, React Router `<Link>`, or a plain `<a>`).
   */
  renderLink: (props: { children: React.ReactNode; path: string; search?: Record<string, unknown> }) => React.ReactNode;
  /** Optional element pinned above the nav items list (e.g. a full-width "New" action button). Purely presentational — the caller builds the element and owns its gating/behavior. */
  topAction?: React.ReactNode;
  /** Translation function — maps an i18n key to the localized string. Typically `t` from `useTranslation()`. */
  translate: (key: string) => string;
}

/**
 * Single navigation item — extracted so each item can call `useActivePath`
 * independently (hooks must be called at the top level of a component).
 */
function SidebarNavItem({
  item,
  renderLink,
  translate,
}: {
  item: NavigationItem;
  renderLink: SidebarNavProps['renderLink'];
  translate: SidebarNavProps['translate'];
}): React.JSX.Element {
  const matchesActivePath = useActivePath(item.path);
  const isActive = !item.isAction && matchesActivePath;
  const Icon = item.icon;
  const label = translate(item.labelKey);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        className="gap-3 px-3 py-2.5 text-base text-sidebar-foreground/70"
        isActive={isActive}
        tooltip={label}
      >
        {renderLink({
          children: (
            <>
              <Icon className={cn('size-5', isActive && 'text-sidebar-primary')} />
              <span className="flex-1 font-medium">{label}</span>
            </>
          ),
          path: item.path,
          search: item.search,
        })}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SidebarNav({
  className,
  hasPermission,
  items,
  renderLink,
  topAction,
  translate,
}: SidebarNavProps): React.JSX.Element {
  const visibleItems = hasPermission
    ? items.filter((item) => !item.requiredPermission || hasPermission(item.requiredPermission))
    : items;

  return (
    <SidebarGroup className={cn('space-y-1 p-0', className)}>
      {topAction && <div className="mb-2">{topAction}</div>}
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => (
            <SidebarNavItem key={item.labelKey} item={item} renderLink={renderLink} translate={translate} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
